import React, { useCallback, useEffect, useState } from 'react';
import { ArrowPathIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import LoadingSpinner from '../../common/LoadingSpinner';
import StatusBanner from '../../ui/StatusBanner';
import { adminAnalyticsAPI } from '../../../services/api';
import { AnalyticsSnapshot, CancellationReport } from '../../../types/analytics';
import SnapshotTab from './SnapshotTab';
import CancellationsTab from './CancellationsTab';
import ClinicCalendarTab from './ClinicCalendarTab';
import { currentPeriod, periodLabel, shiftPeriod } from './format';
import { apiErrorMessage } from '../../../utils/apiError';

type Tab = 'snapshot' | 'cancellations' | 'calendar';

const TABS: { id: Tab; label: string }[] = [
  { id: 'snapshot', label: 'Snapshot' },
  { id: 'cancellations', label: 'Cancellations' },
  { id: 'calendar', label: 'Clinic calendar' },
];

/**
 * Admin Analytics. Everything here is a snapshot "as of now" — the header
 * always says when — and every figure separates what has happened from what
 * is only scheduled.
 */
const AnalyticsPage: React.FC = () => {
  const [tab, setTab] = useState<Tab>('snapshot');
  const [period, setPeriod] = useState(currentPeriod());
  const [snapshot, setSnapshot] = useState<AnalyticsSnapshot | null>(null);
  const [report, setReport] = useState<CancellationReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (tab === 'calendar') return; // the calendar loads itself per week
    try {
      setLoading(true); setError(null);
      if (tab === 'snapshot') setSnapshot(await adminAnalyticsAPI.getSnapshot(period));
      else setReport(await adminAnalyticsAPI.getCancellations());
    } catch (e: any) {
      setError(apiErrorMessage(e, 'Could not load analytics'));
    } finally {
      setLoading(false);
    }
  }, [tab, period]);

  useEffect(() => { load(); }, [load]);

  const asOf = tab === 'snapshot' ? snapshot?.as_of : tab === 'cancellations' ? report?.as_of : undefined;

  return (
    <div>
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-clinic-dark font-body">Analytics</h1>
          <p className="text-sm text-clinic-muted mt-1" data-testid="as-of">
            {asOf ? `Snapshot as of ${new Date(asOf).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}` : 'Live booking data'}
          </p>
        </div>
        {tab !== 'calendar' && (
          <button type="button" onClick={load} disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-clinic-primary-lighter bg-white hover:bg-clinic-cream disabled:opacity-50">
            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        )}
      </div>

      {/* One row of controls above the content: view, then the period it applies to. */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div className="flex rounded-lg bg-clinic-surface-sunken p-1" role="tablist" aria-label="Analytics view">
          {TABS.map(t => (
            <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}
              className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                tab === t.id ? 'bg-white text-clinic-dark shadow-clinic-sm' : 'text-clinic-muted hover:text-clinic-dark'}`}>
              {t.label}
            </button>
          ))}
        </div>
        {tab === 'snapshot' && (
          <div className="flex items-center gap-2">
            <button type="button" aria-label="Previous month" onClick={() => setPeriod(p => shiftPeriod(p, -1))}
              className="p-1.5 rounded-lg border border-clinic-primary-lighter bg-white hover:bg-clinic-cream"><ChevronLeftIcon className="h-4 w-4" /></button>
            <span className="min-w-[9rem] text-center text-sm font-medium text-clinic-dark">{periodLabel(period)}</span>
            <button type="button" aria-label="Next month" onClick={() => setPeriod(p => shiftPeriod(p, 1))}
              className="p-1.5 rounded-lg border border-clinic-primary-lighter bg-white hover:bg-clinic-cream"><ChevronRightIcon className="h-4 w-4" /></button>
            {period !== currentPeriod() && (
              <button type="button" onClick={() => setPeriod(currentPeriod())} className="text-xs text-clinic-primary hover:text-clinic-primary-dark ml-1">
                This month
              </button>
            )}
          </div>
        )}
      </div>

      {error && <div className="mb-4"><StatusBanner variant="error" title="Analytics unavailable" message={error} /></div>}

      {tab === 'calendar' ? (
        <ClinicCalendarTab />
      ) : loading && !(tab === 'snapshot' ? snapshot : report) ? (
        <div className="flex justify-center py-16"><LoadingSpinner /></div>
      ) : tab === 'snapshot' ? (
        snapshot && <SnapshotTab snapshot={snapshot} />
      ) : (
        report && <CancellationsTab report={report} />
      )}
    </div>
  );
};

export default AnalyticsPage;
